import { BUSINESS_TZ_OFFSET_MIN } from './modules';

const NBSP = ' ';

function group(intStr: string): string {
  return intStr.replace(/\B(?=(\d{3})+(?!\d))/g, NBSP);
}

export function toNumber(v: number | string | null | undefined): number {
  if (v === null || v === undefined || v === '') return 0;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : 0;
}

/** 1250000 → "1 250 000 so'm" (butun so'mgacha yaxlitlanadi). */
export function formatMoney(v: number | string | null | undefined, opts: { currency?: boolean; sign?: boolean } = {}) {
  const n = toNumber(v);
  const abs = group(Math.round(Math.abs(n)).toString());
  const sign = n < 0 ? '−' : opts.sign && n > 0 ? '+' : '';
  return `${sign}${abs}${opts.currency === false ? '' : `${NBSP}so‘m`}`;
}

/** Katta sonlarni qisqa ko'rinishda: 12 300 000 → "12,3 mln". */
export function formatCompact(v: number | string | null | undefined): string {
  const n = toNumber(v);
  const abs = Math.abs(n);
  const s = n < 0 ? '−' : '';
  const fmt = (x: number) => (x >= 100 ? Math.round(x).toString() : x.toFixed(1).replace(/\.0$/, '').replace('.', ','));
  if (abs >= 1e9) return `${s}${fmt(abs / 1e9)} mlrd`;
  if (abs >= 1e6) return `${s}${fmt(abs / 1e6)} mln`;
  if (abs >= 1e3) return `${s}${fmt(abs / 1e3)} ming`;
  return `${s}${Math.round(abs)}`;
}

export function formatNumber(v: number | string | null | undefined, digits = 0): string {
  const n = toNumber(v);
  const [i, f] = Math.abs(n).toFixed(digits).split('.');
  return `${n < 0 ? '−' : ''}${group(i!)}${f ? `,${f}` : ''}`;
}

export function formatPercent(v: number | null | undefined, digits = 1): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : v < 0 ? '−' : ''}${Math.abs(v).toFixed(digits).replace('.', ',')}%`;
}

/**
 * ULUSH foizi: "95%". `formatPercent` dan farqi — ishorasiz. Ishora o'sish-pasayish uchun
 * ma'noli ("+6%"), ulush uchun esa yolg'on: yetkazuvchanlik 95% — bu o'sish emas, holat.
 */
export function formatRate(v: number | null | undefined, digits = 0): string {
  if (v === null || v === undefined || !Number.isFinite(v)) return '—';
  return `${v.toFixed(digits).replace('.', ',')}%`;
}

/** Telefonni "+998XXXXXXXXX" ko'rinishiga keltiradi; noto'g'ri bo'lsa null. */
export function normalizePhone(input: string | null | undefined): string | null {
  if (!input) return null;
  let d = input.replace(/\D/g, '');
  if (d.length === 9) d = `998${d}`;
  if (d.length === 12 && d.startsWith('998')) return `+${d}`;
  return null;
}

/** "+998901234567" → "+998 90 123 45 67". */
export function formatPhone(p: string | null | undefined): string {
  if (!p) return '—';
  const n = normalizePhone(p);
  if (!n) return p;
  return `+998 ${n.slice(4, 6)} ${n.slice(6, 9)} ${n.slice(9, 11)} ${n.slice(11, 13)}`;
}

// ------------------------------------------------------------------ Sana/vaqt
// O'zbekistonda yozgi vaqt yo'q: Asia/Tashkent = UTC+5 doimiy. Shuning uchun Intl o'rniga
// qat'iy siljish ishlatiladi — server va brauzerda bir xil natija.

const pad = (n: number) => n.toString().padStart(2, '0');

function toBizDate(d: Date | string | number): Date {
  const date = d instanceof Date ? d : new Date(d);
  return new Date(date.getTime() + BUSINESS_TZ_OFFSET_MIN * 60_000);
}

/** Biznes kuni "YYYY-MM-DD" (Toshkent bo'yicha). */
export function businessDay(d: Date | string | number = new Date()): string {
  const t = toBizDate(d);
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** "YYYY-MM-DD" yoki Date → "dd.mm.yyyy". */
export function formatDate(d: Date | string | number | null | undefined): string {
  if (d === null || d === undefined || d === '') return '—';
  if (typeof d === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(d)) {
    const [y, m, day] = d.split('-');
    return `${day}.${m}.${y}`;
  }
  const t = toBizDate(d);
  if (Number.isNaN(t.getTime())) return '—';
  return `${pad(t.getUTCDate())}.${pad(t.getUTCMonth() + 1)}.${t.getUTCFullYear()}`;
}

export function formatTime(d: Date | string | number | null | undefined): string {
  if (d === null || d === undefined || d === '') return '—';
  const t = toBizDate(d);
  if (Number.isNaN(t.getTime())) return '—';
  return `${pad(t.getUTCHours())}:${pad(t.getUTCMinutes())}`;
}

export function formatDateTime(d: Date | string | number | null | undefined): string {
  if (d === null || d === undefined || d === '') return '—';
  return `${formatDate(d)} ${formatTime(d)}`;
}

/** Biznes kunining UTC dagi boshlanishi (kun chegarasi so'rovlari uchun). */
export function businessDayStart(day: string): Date {
  return new Date(Date.parse(`${day}T00:00:00Z`) - BUSINESS_TZ_OFFSET_MIN * 60_000);
}

export function addDays(day: string, n: number): string {
  const t = new Date(Date.parse(`${day}T00:00:00Z`) + n * 86_400_000);
  return `${t.getUTCFullYear()}-${pad(t.getUTCMonth() + 1)}-${pad(t.getUTCDate())}`;
}

/** Daqiqalarni "8 soat 30 daq" ko'rinishida. */
export function formatMinutes(min: number | null | undefined): string {
  if (!min) return '0';
  const h = Math.floor(min / 60);
  const m = Math.round(min % 60);
  return h ? `${h} soat${m ? ` ${m} daq` : ''}` : `${m} daq`;
}
